// Workbook-faithful layout for the exported sheets (INFS-1454).
//
// Everything here is read off "Data presentation KJP (Skop R01, R02& R06) -
// Tahun 2026.xlsx" — the same source titles.js, colors.js and columns.js were
// taken from, and a sibling of them for the same reason: this is workbook
// layout data, not logic, and the export should read it rather than inline it.
//
// The figures themselves are NOT here. Every number the derivation block shows
// is already computed by src/kjp.js and is passed in; this file only says what
// the columns are called, in what order, and how wide.

// The four roll-up column headers, verbatim from the workbook.
//
// Each carries its unit on a SECOND LINE after a newline — the workbook writes
// "JUMLAH KUOTA DIGUNAPAKAI TAHUNAN\n(m²)", not "... TAHUNAN (m²)". That is
// also why those columns wrap: the line break is the workbook's own, not an
// artefact of column width. The unit is the scope's (m² for R01/R06, KM for
// R02), which is why this is keyed by scope rather than written once.
//
// The export previously shortened these to "JUMLAH SEHINGGA <MONTH>", which is
// where the exported file stopped looking like the workbook it is meant to
// mirror.
export function rollupHeaders(scope, asAtLabel) {
  const unit = scope === 'R02' ? '(KM)' : '(m²)'

  return [
    `JUMLAH KUOTA DIGUNAPAKAI BULANAN SEHINGGA ${asAtLabel}\n${unit}`,
    `BAKI KUOTA BULANAN BELUM DIGUNAPAKAI SEHINGGA ${asAtLabel}\n${unit}`,
    `JUMLAH KUOTA DIGUNAPAKAI TAHUNAN\n${unit}`,
    `BAKI KUOTA TAHUNAN BELUM DIGUNAPAKAI\n${unit}`,
  ]
}

// Column widths, in Excel's character units, taken from each source sheet's
// own <cols> block. The workbook does not use one width everywhere: MAKLUMAT
// is the widest column on every sheet because it carries the sub-row labels,
// the month columns are uniform, and the four roll-ups are wide enough for the
// two-line headers above to wrap sensibly rather than spill.
//
// `lead` is per leading column in that scope's own order (see columns.js), so
// the arrays differ in length: R01 has four leading columns, R02 and R06 three.
export const EXPORT_WIDTHS = {
  R01: { lead: [22.9, 14.4, 35.1, 12.6], month: 14, rollup: 24.6 },
  R02: { lead: [22.9, 29.6, 11.4], month: 14, rollup: 21.9 },
  R06: { lead: [18, 30.9, 10.6], month: 12.6, rollup: 23.1 },
}

// The derivation block — the workbook's own working for how a district's
// contract road length becomes its monthly quota.
//
// Each scope's sheet carries a different set of helper columns, and this
// reproduces each one rather than imposing a single shared layout:
//
//   R01  L (M) → L X 7 (m) → ×1% → the 60/10/30 work-type split
//   R02  L (M) → ×30% / ×10% per road class   (no area factor: R02 is
//        contracted by length, not by area, so it never multiplies by 7)
//   R06  L (M) → L X 7 (m) → ×0.7% / ×0.3% per road class
//
// `banner` is the merged title the workbook puts above the block. R01's two
// percentage headers are the literal numbers 0.01 and 0.005 in the source,
// not text — reproduced as numbers so the sheet matches.
export const DERIVATION_BLOCKS = {
  R01: {
    banner: 'R01- PAVEMENT',
    columns: ['L (M)', 'L X 7 (m)', 0.01, 0.005, 'Purata Setahun', 'Purata Sebulan'],
    widths: [16, 16, 10, 10, 16, 16],
  },
  R02: {
    banner: 'R02- ROAD SHOULDER',
    columns: ['L (M)', '%', 'Purata Setahun (m)', 'Purata Sebulan (m)'],
    widths: [16, 8, 19, 19],
  },
  R06: {
    banner: 'R06- ROADLINE',
    columns: ['L (M)', 'L X 7 (m)', '%', 'Kuota Setahun (m2)', 'Purata Sebulan (m2)'],
    widths: [16, 16, 8, 19, 19],
  },
}

// The block's rows for one district, built from figures src/kjp.js already
// produced. `lengths` is districtLengths() output (metres); `annual` is
// annualQuota() for the scope; `monthCount` the contract length.
//
// Returns one array per sheet row, matching that scope's `columns` above.
// Kept pure and in this file rather than inside the Vue component so
// scripts/verify-kjp.mjs can assert the numbers against the workbook.
export function derivationRows({ scope, lengths, annual, monthCount, split }) {
  const perMonth = (v) => (monthCount ? v / monthCount : 0)
  const { pu, sm } = lengths

  if (scope === 'R02') {
    // Contracted by length: 30% of protocol/primary, 10% of secondary/minor.
    // annualQuota returns km for R02, so the block's metre figures come from
    // the lengths directly and the total is scaled back up to match.
    return [
      ['Protokol/Utama', pu, 0.3, pu * 0.3, perMonth(pu * 0.3)],
      ['Sekunder/Minor', sm, 0.1, sm * 0.1, perMonth(sm * 0.1)],
      ['', '', '', annual * 1000, perMonth(annual * 1000)],
    ]
  }

  if (scope === 'R06') {
    // Contracted by area: length × 7m carriageway, then 0.7% / 0.3%.
    return [
      ['Protokol/Utama', pu, pu * 7, 0.007, pu * 7 * 0.007, perMonth(pu * 7 * 0.007)],
      ['Sekunder/Minor', sm, sm * 7, 0.003, sm * 7 * 0.003, perMonth(sm * 7 * 0.003)],
      ['', pu + sm, (pu + sm) * 7, '', annual, perMonth(annual)],
    ]
  }

  // R01: one area quota for the district, then split 60/10/30 across the work
  // types. The workbook labels the last two 20% and 30%, but 60/10/30 is what
  // reproduces every cell — see CONTRACT_TERMS in src/kjp.js. The labels below
  // are the workbook's; the arithmetic is the real one.
  const total = pu + sm

  return [
    ['Jumlah', total, total * 7, annual, annual / 2, annual, perMonth(annual)],
    ['Pothole (1x1m)', '', '', split.hotmix, '', '', perMonth(split.hotmix)],
    ['Heavy Patching', '', '', 0.2, split.heavy_patching, '', perMonth(split.heavy_patching)],
    ['Cold Asphalt', '', '', 0.3, split.coldmix, '', perMonth(split.coldmix)],
  ]
}
